import React, { useContext, useEffect } from 'react';
import { AuthContext } from 'context/authContext';
import dayjs from 'dayjs';
import { Avatar, MultiSelect, Select, TextInput } from '@mantine/core';
import { Controller, useForm } from 'react-hook-form';
import { DatePicker } from '@mantine/dates';
import PrimaryButton from 'components/primaryButton';
import { useParams } from 'react-router-dom';

import useGetAllTaskboardMembers from '../hooks/useGetAllTaskboardMembers';
import useAddTask from '../hooks/useAddTask';
import useGetAllBuckets from '../hooks/useGetAllBuckets';

import { ReactComponent as CalendarIcon } from 'assets/icons/calenderIcon.svg';
import { ReactComponent as MeetIcon } from 'assets/icons/MeetIcon.svg';
import { ReactComponent as DownArrow } from 'assets/icons/downArrow.svg';
import { ReactComponent as BucketIcon } from 'assets/icons/bucketIcon.svg';

export default function AddTaskPopover({
  bucketDetails,
  setisAddPopoverOpen,
  name,
}) {
  const { authState } = useContext(AuthContext);
  const { id } = useParams();

  const defaultValues = {
    task_board_id: id,
    bucket_id: '',
    name: '',
    created_by_id: '',
    updated_by_id: '',
    end_date: '',
    members: [],
  };

  const {
    handleSubmit,
    control,
    // watch,
    reset,
    setValue,
    formState: { errors },
    watch,
  } = useForm({ defaultValues });

  const getTaskBoardMembers = useGetAllTaskboardMembers(id);
  const addTaskMutation = useAddTask(reset);
  const getBucketsData = useGetAllBuckets(id);

  // error message
  const getErrorMessage = (name) => {
    return (
      errors[name] && (
        <p className=' text-xs font-semibold text-red-500'>
          {errors[name]?.message}
        </p>
      )
    );
  };

  const onSubmit = (data) => {
    data['created_by_id'] = authState?.user_id;
    data['updated_by_id'] = authState?.user_id;
    if (data?.end_date !== null && data?.end_date !== '') {
      data['end_date'] = dayjs(data?.end_date).format('YYYY-MM-DD');
    } else {
      delete data?.end_date;
    }
    addTaskMutation.mutate(data);
  };

  //   custom dropdown item for select
  const SelectItem = ({
    value,
    label,
    image,
    name,
    description,
    ...others
  }) => {
    return (
      <div {...others} className='flex items-center py-2 cursor-pointer'>
        <Avatar src={image} color='red'>
          {label[0]?.toUpperCase()}
        </Avatar>
        <div className='pl-1'>
          <p className='text-18'>{label}</p>
          <p className='text-xs text-gray3 pt-0.5'>{description}</p>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (name === 'bucket') {
      setValue('bucket_id', bucketDetails?.bucket?.bucket_id);
    } else if (name === 'assigned_to') {
      setValue(
        'members',
        bucketDetails?.assignee?.employee_id
          ? [bucketDetails?.assignee?.employee_id]
          : []
      );
    } else if (name === 'progress') {
      setValue('progress', bucketDetails?.progress?.name);
    } else if (name === 'priority') {
      setValue('priority', bucketDetails?.priority?.name);
    } else {
      if (bucketDetails?.due_date?.name === 'Today') {
        setValue('end_date', new Date());
      } else if (bucketDetails?.due_date?.name === 'Late') {
        setValue('end_date', dayjs(new Date()).subtract(1, 'day')?.$d);
      } else if (bucketDetails?.due_date?.name === 'Tomorrow') {
        setValue('end_date', dayjs(new Date()).add(1, 'day')?.$d);
      } else if (bucketDetails?.due_date?.name === 'This Week') {
        setValue('end_date', dayjs(new Date()).endOf('week')?.$d);
      } else if (bucketDetails?.due_date?.name === 'Next Week') {
        const nextWeek = dayjs(new Date()).add(1, 'week')?.$d;
        setValue('end_date', dayjs(new Date(nextWeek)).startOf('week')?.$d);
      } else if (bucketDetails?.due_date?.name === 'Future') {
        const nextWeek = dayjs(new Date()).add(2, 'week')?.$d;
        setValue('end_date', dayjs(new Date(nextWeek)).startOf('week')?.$d);
      } else {
        return setValue('end_date', null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bucketDetails]);

  useEffect(() => {
    if (addTaskMutation?.isSuccess) {
      setisAddPopoverOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addTaskMutation?.isSuccess]);

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div>
          <Controller
            control={control}
            name='name'
            rules={{
              required: 'Required',
              validate: () =>
                watch('name').length < 249 ||
                'maximum length exceded (250 chars)',
            }}
            render={({ field }) => (
              <TextInput
                {...field}
                size='sm'
                placeholder='Enter a task name'
                classNames={{
                  input:
                    ' text-sm bg-transparent bg-[#F9F4F4]  text-normal rounded border-none',
                  label: 'text-[#8E8E8E] text-sm font-normal ',
                  wrapper: 'border-b-2 border-primary',
                }}
              />
            )}
          />
          {getErrorMessage('name')}
        </div>
        <div className='flex mt-2 items-start'>
          <CalendarIcon className='w-6 mt-2.5' />
          <div>
            <Controller
              control={control}
              name='end_date'
              // rules={{
              //   required: 'Required',
              // }}
              defaultValue={new Date()}
              render={({ field }) => (
                <DatePicker
                  {...field}
                  size='md'
                  icon={
                    <p className='text-[#000000] text-sm pl-12 whitespace-nowrap'>
                      Due date -{' '}
                    </p>
                  }
                  clearable={false}
                  placeholder='Select date'
                  minDate={dayjs(new Date()).toDate()}
                  classNames={{
                    input: 'border-primary text-sm border-none pl-24 pr-0',
                    placeHolder: 'text-[#000000]',
                    yearPickerControlActive: 'text-white bg-primary',
                    monthPickerControlActive: 'text-white bg-primary',
                  }}
                />
              )}
            />
            <div className='-mt-2 z-10 relative -ml-5'>
              {getErrorMessage('end_date')}
            </div>
          </div>
        </div>
        {name === 'assigned_to' ? null : (
          <div className='flex items-start justify-between space-x-2 mt-1'>
            <MeetIcon className='w-6  mt-2 pl-1' />
            <div className='w-full pr-4 h-max max-w-[15vw]'>
              <Controller
                control={control}
                name='members'
                // rules={{
                //   required: 'Required',
                // }}
                render={({ field }) => (
                  <MultiSelect
                    {...field}
                    size='md'
                    itemComponent={SelectItem}
                    placeholder='Assign members'
                    data={
                      !getTaskBoardMembers?.data?.data?.length
                        ? []
                        : getTaskBoardMembers?.data?.data?.map((item) => {
                            return {
                              value: `${item?.employee_id}`,
                              label: item?.employee__user__first_name,
                              image: item?.employee__profile_picture,
                              description: item?.employee__designation__name,
                            };
                          })
                    }
                    rightSection={<DownArrow />}
                    styles={{ rightSection: { pointerEvents: 'none' } }}
                    classNames={{
                      input:
                        ' truncate min-h-[7vh]  text-sm border-none p-0  scroller',
                      wrapper: 'border-b border-primary',
                      placeHolder: 'text-sm',
                    }}
                  />
                )}
              />
              <div className='-ml-6'>{getErrorMessage('members')}</div>
            </div>
          </div>
        )}
        {name === 'bucket' ? null : (
          <div className='flex items-center space-x-2 mt-1'>
            <BucketIcon className='w-5' />
            <div className='w-full pr-4'>
              <Controller
                control={control}
                rules={{
                  required: 'Required',
                }}
                name='bucket_id'
                render={({ field }) => (
                  <Select
                    {...field}
                    size='md'
                    placeholder='Select bucket'
                    data={
                      !getBucketsData?.data?.data?.length
                        ? []
                        : getBucketsData?.data?.data?.map((item) => {
                            return {
                              value: `${item?.id}`,
                              label: item?.name,
                            };
                          })
                    }
                    rightSection={<DownArrow />}
                    styles={{ rightSection: { pointerEvents: 'none' } }}
                    classNames={{
                      input: ' truncate h-[7vh] text-sm border-none p-0 pl-2',
                      wrapper: 'border-b border-primary',
                      placeHolder: 'text-sm',
                    }}
                  />
                )}
              />
              <div className='-ml-6'>{getErrorMessage('bucket_id')}</div>
            </div>
          </div>
        )}
        <div className='mt-5'>
          <button className='w-full -mb-4'>
            <PrimaryButton isLoading={addTaskMutation?.isLoading}>
              Add task
            </PrimaryButton>
          </button>
        </div>
      </form>
    </div>
  );
}
